
import { CheckCircleIcon, InfoIcon } from 'vue-feather-icons'
import { sentTelemetryEvent } from '~/fc/TelemetryEvents'
import CustomPlanDialog from /* webpackChunkName: "CustomPlanDialog" */ '~/layouts/partials/pricing/Plan/CustomPlanDialog'
import { formatNumberWithCommas } from '~/fc/Helpers/NumericHelpers';
import { priceWithCurrency } from '~/fc/PaymentHelper'

export default {
    name: 'SinglePlan',
    components: {
        CheckCircleIcon, InfoIcon, CustomPlanDialog,
        ArrowRightIcon: () => import(/* webpackChunkName: "ArrowRightIcon" */ '~/components/ui/icon/ArrowRightIcon'),
        FireIcon: () => import(/* webpackChunkName: "FireIcon" */ '~/components/ui/icon/FireIcon'),
    },
    props: ['item', 'group', 'activeSubscriptions', 'discountAmount', 'couponApplied', 'selectedType', 'planEligibility'],
    data: () => ({
        cpuMinutes: 0,
        planType: 'custom_annual'
    }),
    computed: {
        type() { return this.item.type },
        userLoggedIn() { return this.$auth ? this.$auth.Token : false },
        canChangeSubscription() { return this.$store.state.canChangeSubscription },
        isCustomPlan() { return this.item.group.includes('custom') },
        priceAmountStr() { return this.isCustomPlan ? this.couponApplied ? '' : this.$t("flexible") : `${priceWithCurrency(this.priceAmount, this.item?.currency)}` },
        priceAfterDiscountStr() { return this.isCustomPlan ? this.$t("flexible") : `${priceWithCurrency(this.priceAfterDiscount, this.item?.currency)}` },        
        isAnnualPlan() { return Boolean(this.item && this.item?.group.includes('annual') && !this.isCustomPlan)},
        isPopularPlan() { return Boolean(this.item && this.item?.group.includes('pro'))},
        userSubscription() { return this.$store.state.subscription },
        priceToLong() { return this.$store.state.store.priceToLong },
        priceAfterDiscount() {
            if (this.couponApplied && this.discountAmount) return parseFloat(this.priceAmount - (this.priceAmount * this.discountAmount / 100)).toFixed(2)            
            if (this.isAnnualPlan) return parseFloat(this.item.price.amount / 12).toFixed(2)            
            return this.priceAmount
        },
        priceAmount() {
            const { item } = this
            if (!item.price) { return 0 }
            if (this.isAnnualPlan) return item.monthlyPrice || parseFloat(item.price.amount / 10).toFixed(2)
            if (item.price.schema === 'fixed') { return item.price.amount }
            if (!item.price.tiers) { return 0 }
            const tierAmount = this.customPlan?.price?.tiers?.find(tier => parseInt(tier.start) * 100 == this.cpuMinutes)?.amount
            if (!tierAmount) return 0

            return parseFloat(tierAmount * parseFloat(this.cpuMinutes) / 100).toFixed(2)
        },
        customPlan() {
            if (!Array.isArray(this.group)) return {};
            // if the group has only one plan, don't need to search
            if (this.group.length === 1) return this.group[0]

            let plan = {}
            if (this.planType === 'custom_annual') plan = this.group.find(plan => plan.group.includes('annual'))
            else if (this.planType === 'custom_subscription') plan = this.group.find(plan => plan.type === 'unified-subscription')
            else if (this.planType === 'custom_credit') plan = this.group.find(plan => plan.type === 'unified-credit')
            
            return plan || this.group[0]
        },
        /**
         * Determines if a user is an USD package is on a localized pricing page.
         *
         * @returns {boolean} True if the user has a USD package and is not on a localized pricing page; otherwise, false.
         */
        isNoneLocaleUserOnLocalePage() {
            if(!this.userSubscription.subscription || !this.userSubscription.subscription.hasOwnProperty('isSelectedCurrencyAllowed')) return false
            return !this.userSubscription.subscription?.isSelectedCurrencyAllowed;
        },
        /**
         * Determines whether the user can continue based on their login status, 
         * subscription type, and eligibility criteria.
         *
         * @returns {boolean} Returns `true` if the user can proceed, otherwise `false`.
         */
        canContinue() {
            // if user is not logged in, he can proceed 
            if (!this.userLoggedIn) return true
            if (this.isNoneLocaleUserOnLocalePage) return false
            return !this.isCustomPlan && this.eligibility ? this.eligibility.eligible : true
        },
        /**
         * Determines the text for the continue CTA button based on eligibility, locale, and plan status.
         * @returns {string} Translated text for the CTA button.
         */
        continueCTAText() {
            if (!(this.eligibility ? this.eligibility.eligible : true)) return this.$t('current_plan')
            if (this.isNoneLocaleUserOnLocalePage) return this.$t('get_started')
            if (!this.canContinue) return this.$t('current_plan')
            return this.$t('get_started')
        },
        eligibility () {
            return this.planEligibility?.hasOwnProperty(this.item.id || this.item._id) 
                ? this.planEligibility[this.item.id || this.item._id] : false
        },
        /**
         * This space is needed to aligh the CTA buttons
         * when the discount on the other cards extend to the next line.
         */
        showSpaceUnderCustomPlan () {
            return this.isCustomPlan && this.selectedType === 'annual' && this.priceToLong
        }
    },
    mounted() {
        if(this.priceAfterDiscountStr.length > 8) this.$store.commit('store/setPriceToLong', true)
    },
    methods: {
        onSelect() {
            if (!this.canContinue || this.isNoneLocaleUserOnLocalePage) { return }
            if (this.isCustomPlan) this.openCustomPlanDialog({ 'planType': this.selectedType === 'monthly' ? 'custom_subscription' : 'custom_annual' })
            else this.selectPlan(this.item);
        },
        selectCustomPlan({ plan, cpuMinutes, maxFileSizeInGb, amount }) {
            this.cpuMinutes = cpuMinutes

            const price = {
                schema: plan.price.schema,
                quantity: plan.price.schema === 'fixed' ? 1 : this.cpuMinutes,
                amount: this.priceAmount
            }

            const item = { ...plan, price: price, maxFileSizeInGb: maxFileSizeInGb }

            this.selectPlan(item);
        },
        selectPlan(plan) {
            if (!plan) return
            this.$gtm.push({
                event: "add_package",
                package_name: plan.name,
                package_type: plan.type,
            });
            sentTelemetryEvent('add_package', { package_type: plan.type, package_name: plan.name })

            this.$emit('select', {
                ...plan,
                price: {
                    schema: plan.price.schema,
                    quantity: plan.price.schema === 'fixed' ? 1 : this.cpuMinutes,
                    amount: this.isAnnualPlan ? parseFloat(this.priceAmount * 10).toFixed(2) : this.priceAmount
                }
            })
        },
        getFileSizeLabel(label) {
            return label ? label.replace('GB', ' GB').replace('MB', ' MB') : ''
        },
        getFileSizeName(packageName) {
            switch (packageName) {
                case 'Pro':
                    return 'Highest'
                case 'Standard':
                    return 'Large'
                default:
                    return 'Medium'
            }
        },
        getPriority(p) {
            switch (p) {
                case 'high':
                    return this.$t('high')
                case 'highest':
                    return this.$t('highest')
                default:
                    return 'Normal'
            }
        },
        openCustomPlanDialog() {
            this.$refs.CustomPlanDialog.open({ 'planType': this.selectedType === 'monthly' ? 'custom_subscription' : 'custom_annual' })
        },
        formatNumberWithCommas(str) {
            return formatNumberWithCommas(formatNumberWithCommas(str))
        }
    },
}
